import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Shield, CheckCircle, SpinnerGap, ArrowRight, Warning } from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_compliance-hub-623/artifacts/04kw0jh0_IMG_4683.jpeg';

export const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { refreshUser, user } = useAuth();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);
  const pollingRef = useRef(null);
  
  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    if (attempts >= 15) {
      setStatus('error');
      return;
    }
    
    try {
      const { data } = await axios.get(
        `${API}/api/payments/checkout-status/${sessionId}`,
      );
      
      console.log('Payment poll response:', data);
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        // Try to refresh user session
        try {
          await refreshUser();
        } catch (e) {
          console.log('Could not refresh user - may need to log in again');
        }
        return;
      } else if (data.status === 'expired' || data.payment_status === 'error') {
        setStatus('error');
        return;
      } else {
        // Still pending, poll again with increasing delay
        setAttempts(prev => prev + 1);
        const delay = Math.min(2000 + (attempts * 500), 5000);
        pollingRef.current = setTimeout(() => pollPaymentStatus(), delay);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      const delay = Math.min(2000 + (attempts * 500), 5000);
      pollingRef.current = setTimeout(() => pollPaymentStatus(), delay);
    }
  }, [sessionId, attempts, refreshUser]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
    
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, []); // Only run once on mount

  // Trigger poll when attempts change
  useEffect(() => {
    if (attempts > 0 && attempts < 15 && status === 'loading') {
      // polling is handled by setTimeout in pollPaymentStatus
    }
  }, [attempts, status]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <div className="w-full max-w-md text-center">
        {/* Logo */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center space-x-2">
            <img src={LOGO_URL} alt="Vantage" className="h-10 w-auto" />
            <span className="text-xl font-bold tracking-tight">Vantage Fleet</span>
          </Link>
        </div>
        
        {/* Status Card */}
        <div className="border border-border bg-background p-8">
          {status === 'loading' && (
            <>
              <SpinnerGap weight="bold" className="h-16 w-16 text-primary mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-semibold tracking-tight mb-2">Processing Payment</h1>
              <p className="text-muted-foreground mb-6">Please wait while we confirm your subscription...</p>
              <p className="text-xs text-muted-foreground">Checking... ({attempts + 1}/15)</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle weight="duotone" className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-semibold tracking-tight mb-2">Payment Successful!</h1>
              <p className="text-muted-foreground mb-6">Your subscription is now active. Welcome to Vantage Fleet & Compliance.</p>
              {user ? (
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="rounded-sm"
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Dashboard
                    <ArrowRight weight="bold" className="ml-2 h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => navigate('/subscription?welcome=1')}
                    variant="outline"
                    className="rounded-sm"
                    data-testid="view-subscription-btn"
                  >
                    View my subscription
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => navigate('/login')}
                  className="rounded-sm"
                  data-testid="go-to-login-btn"
                >
                  Sign In to Access Dashboard
                  <ArrowRight weight="bold" className="ml-2 h-4 w-4" />
                </Button>
              )}
            </>
          )}
          
          {status === 'error' && (
            <>
              <Warning weight="duotone" className="h-16 w-16 text-destructive mx-auto mb-4" />
              <h1 className="text-2xl font-semibold tracking-tight mb-2">Payment Issue</h1>
              <p className="text-muted-foreground mb-6">We couldn't confirm your payment. If you were charged, your subscription will activate shortly. Please try logging in.</p>
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => navigate('/login')} 
                  className="rounded-sm"
                  data-testid="login-btn"
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => {
                    setStatus('loading');
                    setAttempts(0);
                    pollPaymentStatus();
                  }} 
                  variant="outline"
                  className="rounded-sm"
                  data-testid="retry-btn"
                >
                  Retry Check
                </Button>
                <Link to="/">
                  <Button variant="ghost" className="w-full rounded-sm">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
